import React, {useEffect} from "react";
import {Button, Tooltip, Typography} from "@material-ui/core";
import {
  InfoOutlined,
  Translate,
  UnfoldLess,
  UnfoldMore,
} from "@material-ui/icons";
import clsx from "clsx";
import FlexBox from "view/components/Layout/FlexBox";
import {
  useHasLocalisationContext,
  useLocalisationContext,
} from "./LocalisationProvider";
import useStyles from "./styles";

const DEFAULT_TOOLTIP =
  "Exhibitors see this text in their selected language. Anything left empty falls back to English (UK).";

type Props = {
  tooltip?: string;
  id?: string;
  disableBottomMargin?: boolean;
  alwaysVisible?: boolean;
  withTopMargin?: boolean;
  /**
   * Adds a "{n} languages available" line under the header while the settings
   * are expanded (n = the languages the form can translate into).
   */
  showLanguageCount?: boolean;
  /**
   * Tooltip on the "{n} languages" phrase of that line — a per-Show breakdown
   * of where the languages come from. Falls back to {@link tooltip}.
   */
  availableTooltip?: React.ReactNode;
};

const LocalisationSettingsBarInner = ({
  tooltip = DEFAULT_TOOLTIP,
  id,
  disableBottomMargin,
  alwaysVisible,
  withTopMargin,
  showLanguageCount,
  availableTooltip,
}: Props) => {
  const classes = useStyles();
  const {
    controller,
    sectionCount,
    anyExpanded,
    toggleAll,
    registerBar,
  } = useLocalisationContext();

  const isVisible =
    controller.hasLanguages && (alwaysVisible || sectionCount >= 2);

  useEffect(() => {
    if (!isVisible) return undefined;

    return registerBar();
  }, [isVisible, registerBar]);

  if (!isVisible) return null;

  const languageCount = controller.languages.length;

  return (
    <FlexBox
      column
      fullWidth
      className={clsx(
        classes.settingsBar,
        showLanguageCount && anyExpanded && classes.settingsBarStacked,
        withTopMargin && classes.barTopMargin,
        disableBottomMargin && classes.noBottomMargin
      )}
    >
      <FlexBox
        alignCenter
        justifyBetween
        fullWidth
        className={classes.settingsBarHeader}
      >
        <FlexBox alignCenter gap={1}>
          <Translate color="primary" className={classes.headerIcon} />
          <Typography variant="h3" color="primary">
            Localisation
          </Typography>
          <Tooltip title={tooltip}>
            <InfoOutlined className={classes.infoIcon} />
          </Tooltip>
        </FlexBox>

        <Button
          id={id}
          color="primary"
          className={classes.expandAllButton}
          startIcon={anyExpanded ? <UnfoldLess /> : <UnfoldMore />}
          onClick={toggleAll}
        >
          {anyExpanded ? "Collapse settings" : "Expand settings"}
        </Button>
      </FlexBox>

      {showLanguageCount && anyExpanded && (
        <Typography className={classes.languageCount}>
          <Tooltip title={availableTooltip ?? tooltip}>
            <span className={classes.languageCountEmphasis}>
              {languageCount} {languageCount === 1 ? "language" : "languages"}
            </span>
          </Tooltip>{" "}
          available
        </Typography>
      )}
    </FlexBox>
  );
};

// Renders nothing outside a <LocalisationProvider> (e.g. Library masters,
// which are not translated per show) so shared editors can embed this freely.
const LocalisationSettingsBar = (props: Props) =>
  useHasLocalisationContext() ? (
    <LocalisationSettingsBarInner {...props} />
  ) : null;

export default LocalisationSettingsBar;
